import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/sections/Footer";
import { CookieBanner } from "../components/CookieBanner";
import { useLanguage } from "../contexts/LanguageContext";
import { applySEO } from "../lib/seo";

export default function LegalNotice() {
  const { lang, t } = useLanguage();

  useEffect(() => {
    applySEO({
      title:
        lang === "fr"
          ? "Mentions légales — Richild Invest GBC"
          : "Legal Notice — Richild Invest GBC",
      description:
        lang === "fr"
          ? "Mentions légales du site richildinvest.com — Richild Invest GBC, société d'investissement basée à Port Louis, Île Maurice."
          : "Legal notice for richildinvest.com — Richild Invest GBC, investment firm headquartered in Port Louis, Mauritius.",
      url: "https://richildinvest.com/mentions-legales",
      type: "article",
      lang,
    });
    window.scrollTo(0, 0);
  }, [lang]);

  const content = lang === "fr" ? frContent : enContent;

  return (
    <div data-testid="legal-notice-page" className="bg-white min-h-screen">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#0D2F4C]/70 hover:text-[#0D2F4C] mb-10 transition-colors"
          >
            <ArrowLeft size={14} />
            {t.legal.back}
          </Link>

          <div className="text-xs uppercase tracking-[0.25em] text-[#C9A961] mb-3">
            Richild Invest GBC
          </div>
          <h1
            className="text-4xl sm:text-5xl text-[#0D2F4C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.legal.notice_title}
          </h1>
          <p className="text-sm text-[#0D2F4C]/60 mb-12 border-b border-[#0D2F4C]/10 pb-8">
            {t.legal.notice_updated}
          </p>

          <article
            className="legal-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

const frContent = `
<h2>1. Éditeur du site</h2>
<p>
  Le présent site, accessible à l'adresse <strong>https://richildinvest.com</strong>,
  est édité par :
</p>
<ul>
  <li><strong>Richild Invest GBC</strong></li>
  <li>Société d'investissement internationale</li>
  <li>Siège social : Port Louis, Île Maurice</li>
  <li>E-mail : <a href="mailto:info@richildinvest.com">info@richildinvest.com</a></li>
</ul>

<h2>2. Directeur de la publication</h2>
<p>Le directeur de la publication est la direction de Richild Invest GBC.</p>

<h2>3. Hébergement</h2>
<p>
  Le site est hébergé par <strong>Render Services, Inc.</strong><br>
  525 Brannan St, San Francisco, CA 94107, États-Unis<br>
  Site web : <a href="https://render.com" target="_blank" rel="noopener noreferrer">https://render.com</a>
</p>
<p>
  Les données sont stockées via <strong>MongoDB Atlas</strong> (MongoDB, Inc.) sur des serveurs
  situés dans l'Union européenne (région Paris).
</p>

<h2>4. Propriété intellectuelle</h2>
<p>
  L'ensemble des contenus présents sur le site (textes, images, logos, graphismes,
  vidéos, icônes) sont la propriété exclusive de Richild Invest GBC ou de ses partenaires,
  et sont protégés par le droit d'auteur, le droit des marques et plus généralement par
  le droit de la propriété intellectuelle.
</p>
<p>
  Toute reproduction, représentation, modification, publication, adaptation totale ou
  partielle des éléments du site, quel que soit le moyen ou le procédé utilisé, est
  interdite sans l'autorisation écrite préalable de Richild Invest GBC.
</p>

<h2>5. Liens hypertextes</h2>
<p>
  Le site peut contenir des liens vers d'autres sites internet. Richild Invest GBC n'exerce
  aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.
</p>

<h2>6. Limitation de responsabilité</h2>
<p>
  Les informations diffusées sur le site sont fournies à titre indicatif et ne constituent
  en aucun cas une offre, une recommandation d'investissement ou un conseil financier.
  Richild Invest GBC s'efforce d'assurer l'exactitude et la mise à jour des informations
  diffusées sur ce site, mais ne peut garantir l'absence d'erreurs ou d'omissions.
</p>

<h2>7. Droit applicable et juridiction compétente</h2>
<p>
  Le présent site est régi par le droit mauricien. Tout litige relatif à l'utilisation du
  site sera de la compétence exclusive des tribunaux de Port Louis, Île Maurice.
</p>

<h2>8. Contact</h2>
<p>
  Pour toute question concernant les présentes mentions légales, vous pouvez nous contacter :<br>
  <a href="mailto:info@richildinvest.com">info@richildinvest.com</a>
</p>
`;

const enContent = `
<h2>1. Website publisher</h2>
<p>
  This website, accessible at <strong>https://richildinvest.com</strong>, is published by:
</p>
<ul>
  <li><strong>Richild Invest GBC</strong></li>
  <li>International investment firm</li>
  <li>Registered office: Port Louis, Mauritius</li>
  <li>Email: <a href="mailto:info@richildinvest.com">info@richildinvest.com</a></li>
</ul>

<h2>2. Publication director</h2>
<p>The publication director is the management of Richild Invest GBC.</p>

<h2>3. Hosting</h2>
<p>
  This website is hosted by <strong>Render Services, Inc.</strong><br>
  525 Brannan St, San Francisco, CA 94107, USA<br>
  Website: <a href="https://render.com" target="_blank" rel="noopener noreferrer">https://render.com</a>
</p>
<p>
  Data is stored via <strong>MongoDB Atlas</strong> (MongoDB, Inc.) on servers located in the
  European Union (Paris region).
</p>

<h2>4. Intellectual property</h2>
<p>
  All content on the site (texts, images, logos, graphics, videos, icons) is the exclusive
  property of Richild Invest GBC or its partners, and is protected by copyright, trademark
  law, and more generally by intellectual property law.
</p>
<p>
  Any reproduction, representation, modification, publication, total or partial adaptation
  of the elements of the site, by any means or process, is prohibited without the prior
  written authorization of Richild Invest GBC.
</p>

<h2>5. Hyperlinks</h2>
<p>
  The website may contain links to other websites. Richild Invest GBC has no control over
  these sites and disclaims any responsibility for their content.
</p>

<h2>6. Limitation of liability</h2>
<p>
  The information published on the site is provided for indicative purposes only and does
  not constitute an offer, an investment recommendation, or financial advice. Richild
  Invest GBC strives to ensure the accuracy and updating of the information published on
  this site, but cannot guarantee the absence of errors or omissions.
</p>

<h2>7. Applicable law and jurisdiction</h2>
<p>
  This site is governed by Mauritian law. Any dispute relating to the use of the site
  shall be subject to the exclusive jurisdiction of the courts of Port Louis, Mauritius.
</p>

<h2>8. Contact</h2>
<p>
  For any questions regarding this legal notice, please contact us:<br>
  <a href="mailto:info@richildinvest.com">info@richildinvest.com</a>
</p>
`;
